import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Spinner,
  useDisclosure,
} from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api } from "@/api";
import type {
  ConflictDetail,
  ConflictResolutionRequest,
} from "@/api/accounting/conflicts";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";
import useErrorHandling, { type ErrorResponse } from "@/hooks/useErrorHandling";
import { ConflictDiff } from "./conflictDiff";
import { ConfirmResolveModal } from "./confirmResolveModal";
import {
  conflictActionLocale,
  conflictStatusColors,
  conflictStatusLocale,
  conflictTypeColors,
  conflictTypeLocale,
  entityTypeLocale,
  errorCategoryColors,
  errorCategoryLocale,
  syncErrorCodeLocale,
  syncStageLocale,
} from "./ui";

type ConflictAction = ConflictDetail["availableActions"][number];

const actionColors: Record<
  ConflictAction,
  "primary" | "secondary" | "default" | "success"
> = {
  accept_elastic: "primary",
  accept_1c: "secondary",
  retry: "default",
  select_accounting_entity: "success",
};

// Действия, перезаписывающие данные в одной из систем, требуют подтверждения.
const confirmMessages: Partial<Record<ConflictAction, string>> = {
  accept_elastic:
    "Данные Elastic будут признаны верными и перезапишут запись в 1С.",
  accept_1c: "Данные 1С будут признаны верными и перезапишут запись в Elastic.",
};

interface ResolveConflictModalProps {
  disclosure: ReturnType<typeof useDisclosure>;
  integrationId: number;
  conflictId: number | null;
  onResolved?: () => void;
}

export function ResolveConflictModal({
  disclosure,
  integrationId,
  conflictId,
  onResolved,
}: ResolveConflictModalProps) {
  const [detail, setDetail] = useState<ConflictDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [selectedGuid, setSelectedGuid] = useState<string>("");
  const [pendingAction, setPendingAction] = useState<ConflictAction | null>(
    null,
  );
  const handleError = useErrorHandling();
  const confirmDisclosure = useDisclosure();

  const loadDetail = useCallback(async () => {
    if (!conflictId) return;
    setIsLoading(true);
    try {
      setDetail(await api.accountingConflicts.get(integrationId, conflictId));
    } catch (err) {
      const { errorMessage } = handleError(err as ErrorResponse);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [integrationId, conflictId, handleError]);

  useEffect(() => {
    if (disclosure.isOpen && conflictId) {
      setDetail(null);
      setSelectedGuid("");
      loadDetail();
    }
  }, [disclosure.isOpen, conflictId, loadDetail]);

  const resolve = useCallback(
    async (action: ConflictAction) => {
      if (!conflictId) return;

      const body: ConflictResolutionRequest =
        action === "select_accounting_entity"
          ? { action, accountingGuid: selectedGuid }
          : { action };

      setIsResolving(true);
      try {
        await api.accountingConflicts.resolve(integrationId, conflictId, body);
        toast.success("Конфликт разрешён");
        onResolved?.();
        disclosure.onClose();
      } catch (err) {
        const { errorMessage } = handleError(err as ErrorResponse);

        // 409 — конфликт уже разрешён кем-то ещё либо действие стало неприменимо:
        // перезагружаем детали, чтобы показать актуальное состояние.
        if ((err as { status?: number })?.status === 409) {
          toast.warning(errorMessage);
          onResolved?.();
          loadDetail();
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsResolving(false);
      }
    },
    [
      integrationId,
      conflictId,
      selectedGuid,
      onResolved,
      disclosure,
      handleError,
      loadDetail,
    ],
  );

  const onActionPress = useCallback(
    (action: ConflictAction) => {
      if (confirmMessages[action]) {
        setPendingAction(action);
        confirmDisclosure.onOpen();
        return;
      }
      resolve(action);
    },
    [confirmDisclosure, resolve],
  );

  const candidates = detail?.accountingCandidates || [];
  const errorHistory = detail?.errorHistory || [];

  return (
    <>
      <ConfirmResolveModal
        isOpen={confirmDisclosure.isOpen}
        onOpenChange={confirmDisclosure.onOpenChange}
        message={
          (pendingAction && confirmMessages[pendingAction]) ||
          "Данные будут перезаписаны."
        }
        onConfirm={() => pendingAction && resolve(pendingAction)}
      />

      <Modal
        isOpen={disclosure.isOpen}
        onOpenChange={disclosure.onOpenChange}
        size="5xl"
        scrollBehavior="inside">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex flex-row gap-2 items-center">
                  <span>Конфликт #{conflictId}</span>
                  {detail && (
                    <>
                      <Chip
                        size="sm"
                        variant="flat"
                        color={conflictTypeColors[detail.type] || "default"}>
                        {conflictTypeLocale[detail.type] || detail.type}
                      </Chip>
                      <Chip
                        size="sm"
                        variant="flat"
                        color={
                          conflictStatusColors[detail.status] || "default"
                        }>
                        {conflictStatusLocale[detail.status] || detail.status}
                      </Chip>
                    </>
                  )}
                </div>
                {detail && (
                  <span className="text-small font-normal text-default-500">
                    {entityTypeLocale[detail.entityType] || detail.entityType}
                    {detail.domainId ? ` #${detail.domainId}` : ""} ·{" "}
                    {detail.reason}
                    {detail.errorCode
                      ? ` · ${syncErrorCodeLocale[detail.errorCode] || detail.errorCode}`
                      : ""}
                  </span>
                )}
              </ModalHeader>
              <ModalBody>
                {isLoading || !detail ? (
                  <div className="flex justify-center py-8">
                    <Spinner />
                  </div>
                ) : detail.type === "multiple_accounting_matches" ? (
                  <RadioGroup
                    label="Выберите сущность 1С, с которой связать запись"
                    value={selectedGuid}
                    onValueChange={setSelectedGuid}>
                    {candidates.map((candidate) => (
                      <Radio
                        key={candidate.accountingGuid}
                        value={candidate.accountingGuid}
                        classNames={{
                          base: `max-w-full m-0 mb-2 p-3 border-2 rounded-xl items-start ${
                            candidate.deletionMark
                              ? "border-danger-300"
                              : "border-foreground-200"
                          }`,
                        }}>
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-row gap-2 items-center">
                            <span className="font-medium">
                              {candidate.displayName}
                            </span>
                            {candidate.deletionMark && (
                              <Chip size="sm" variant="flat" color="danger">
                                Помечен на удаление в 1С
                              </Chip>
                            )}
                          </div>
                          <span className="text-tiny text-default-500">
                            ИНН: {candidate.inn || "—"} · ОГРН:{" "}
                            {candidate.ogrn || "—"}
                          </span>
                          <span className="text-tiny text-default-400 font-mono">
                            {candidate.accountingGuid}
                          </span>
                        </div>
                      </Radio>
                    ))}
                    {candidates.length === 0 && (
                      <span className="text-default-400">
                        Кандидаты не найдены
                      </span>
                    )}
                  </RadioGroup>
                ) : detail.type === "failure" ? (
                  <ErrorHistory history={errorHistory} />
                ) : (
                  <div className="flex flex-col gap-4">
                    <ConflictDiff detail={detail} />
                    {errorHistory.length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-small text-default-500">
                          История ошибок ({errorHistory.length})
                        </summary>
                        <div className="mt-2">
                          <ErrorHistory history={errorHistory} />
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Закрыть
                </Button>
                {(detail?.availableActions || []).map((action) => (
                  <Button
                    key={action}
                    color={actionColors[action] || "default"}
                    isLoading={isResolving}
                    isDisabled={
                      action === "select_accounting_entity" && !selectedGuid
                    }
                    onPress={() => onActionPress(action)}>
                    {conflictActionLocale[action] || action}
                  </Button>
                ))}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

function ErrorHistory({
  history,
}: {
  history: ConflictDetail["errorHistory"];
}) {
  return (
    <TableBuilder
      displayOptions={false}
      removeWrapper={true}
      columns={[
        {
          key: "createdAt",
          label: "Дата",
          type: ColumnType.Custom,
          render: (value: string) => (
            <span>
              {new Date(value).toLocaleString("ru-RU", { timeZone: "Etc/Utc" })}
            </span>
          ),
        },
        {
          key: "stage",
          label: "Этап",
          type: ColumnType.Custom,
          render: (value: string) => <>{syncStageLocale[value] || value}</>,
        },
        {
          key: "attempt",
          label: "Попытка",
          type: ColumnType.Number,
        },
        {
          key: "errorCategory",
          label: "Категория",
          type: ColumnType.Custom,
          render: (value: string) => (
            <Chip
              size="sm"
              variant="flat"
              color={errorCategoryColors[value] || "default"}>
              {errorCategoryLocale[value] || value}
            </Chip>
          ),
        },
        {
          key: "errorMessage",
          label: "Ошибка",
          type: ColumnType.Custom,
          render: (
            value: string,
            row: ConflictDetail["errorHistory"][number],
          ) => (
            <div className="flex flex-col gap-1">
              <span>{value}</span>
              {row.context && (
                <details>
                  <summary className="cursor-pointer text-tiny text-default-400">
                    Контекст
                  </summary>
                  <pre className="text-tiny whitespace-pre-wrap break-all">
                    {JSON.stringify(row.context, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ),
        },
      ]}
      data={history}
      rowsPerPage={5}
      defaultSortDescriptor={{ column: "createdAt", direction: "descending" }}
    />
  );
}
